import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  MagnifyingGlassIcon,
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  BriefcaseIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfirm } from "../../../hooks/useConfirm";
import {
  getConsultationsByLawyerWithFilters,
  getConsultationStats,
  acceptConsultation,
  rejectConsultation,
} from "@/shared/db";
import { toast } from "sonner";
import { Button, Input, Select } from "@/shared/ui";
import { ConsultationsTable } from "../components/ConsultationsTable";
import { ConsultationDetailModal } from "../components/ConsultationDetailModal";
import { CreateCaseModal } from "../components/CreateCaseModal";
import { ListPageSkeleton } from "../../../components/skeletons";
import type { ConsultationWithDetails } from "../types/consultation.types";

interface ConsultationStats {
  total: number;
  pending: number;
  accepted: number;
  completed: number;
  rejected: number;
}

type FilterStatus =
  | "all"
  | "pending"
  | "accepted"
  | "rejected"
  | "completed"
  | "cancelled";
type FilterUrgency = "" | "urgent" | "high" | "normal" | "low";

export const ConsultationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { confirm, Modal: ConfirmModal } = useConfirm();

  // State
  const [consultations, setConsultations] = useState<ConsultationWithDetails[]>(
    [],
  );
  const [stats, setStats] = useState<ConsultationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("all");
  const [urgencyFilter, setUrgencyFilter] = useState<FilterUrgency>("");
  const [practiceAreaFilter, setPracticeAreaFilter] = useState("");

  // Modals
  const [isCreateCaseModalOpen, setIsCreateCaseModalOpen] = useState(false);
  const [selectedConsultationDetails, setSelectedConsultationDetails] =
    useState<ConsultationWithDetails | null>(null);
  const [consultationViewModal, setConsultationViewModal] =
    useState<ConsultationWithDetails | null>(null);

  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Load consultations with filters
      const consultationsData = await getConsultationsByLawyerWithFilters(
        user.id,
        {
          status: statusFilter === "all" ? undefined : statusFilter,
          urgency: urgencyFilter || undefined,
          practice_area: practiceAreaFilter || undefined,
          search: searchQuery || undefined,
        },
      );

      setConsultations(consultationsData as unknown as ConsultationWithDetails[]);

      // Load stats
      const statsData = await getConsultationStats(user.id);
      setStats({
        total: statsData.total || 0,
        pending: statsData.pending || 0,
        accepted: statsData.accepted || 0,
        completed: statsData.completed || 0,
        rejected: statsData.rejected || 0,
      });
    } catch (error) {
      console.error("Error loading consultations:", error);
      toast.error(t("lawyer.consultations.loadError"));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [user, statusFilter, urgencyFilter, practiceAreaFilter, searchQuery, t]);

  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user, loadData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
  };

  const handleViewConsultation = (consultationId: string) => {
    const consultation = consultations.find((c) => c.id === consultationId);
    setConsultationViewModal(consultation || null);
  };

  const handleAccept = (consultationId: string) => {
    confirm({
      title: t("lawyer.consultations.accept.title"),
      message: t("lawyer.consultations.confirmAccept.message"),
      confirmText: t("lawyer.consultations.actions.accept"),
      cancelText: t("common.cancel"),
      variant: "info",
      onConfirm: async () => {
        await acceptConsultation(consultationId);
        toast.success(t("lawyer.consultations.accept.success"));
        loadData();
      },
    });
  };

  const handleReject = (consultationId: string) => {
    confirm({
      title: t("lawyer.consultations.reject.title"),
      message: t("lawyer.consultations.confirmReject.message"),
      confirmText: t("lawyer.consultations.actions.reject"),
      cancelText: t("common.cancel"),
      variant: "danger",
      onConfirm: async () => {
        await rejectConsultation(consultationId);
        toast.success(t("lawyer.consultations.reject.success"));
        loadData();
      },
    });
  };

  const handleConvertToCase = (consultation: ConsultationWithDetails) => {
    setSelectedConsultationDetails(consultation);
    setIsCreateCaseModalOpen(true);
  };

  const handleCaseCreated = () => {
    setIsCreateCaseModalOpen(false);
    toast.success(t("lawyer.consultations.convertedToCase"));
    setSelectedConsultationDetails(null);
    loadData();
  };

  const practiceAreas = [
    { code: "civil", name: t("lawyer.practiceAreas.civil") },
    { code: "penal", name: t("lawyer.practiceAreas.penal") },
    { code: "laboral", name: t("lawyer.practiceAreas.laboral") },
    { code: "comercial", name: t("lawyer.practiceAreas.comercial") },
    { code: "fiscal", name: t("lawyer.practiceAreas.fiscal") },
    { code: "administrativo", name: t("lawyer.practiceAreas.administrativo") },
    { code: "familiar", name: t("lawyer.practiceAreas.familiar") },
    {
      code: "inmobiliario",
      name: t("lawyer.practiceAreas.inmobiliario"),
    },
  ];

  if (loading && consultations.length === 0) {
    return <ListPageSkeleton />;
  }

  return (
    <div className="min-h-screen">
      {ConfirmModal}
      <div className="mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-4xl font-bold text-juridico">
            {t("lawyer.consultations.title")}
          </h1>
          <p className="mt-2 text-base text-tiber-700">
            {t("lawyer.consultations.subtitle")}
          </p>
        </motion.div>

        {/* Stats Cards - gradient style */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-linear-to-br from-tiber-500 to-tiber-600 rounded-xl p-5 shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <ChatBubbleLeftRightIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white/80">
                    {t("lawyer.consultations.stats.total")}
                  </p>
                  <p className="text-2xl font-bold text-white">
                    {stats.total}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
              className="bg-linear-to-br from-dorado to-dorado-dark rounded-xl p-5 shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <ClockIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white/90">
                    {t("lawyer.consultations.stats.pending")}
                  </p>
                  <p className="text-2xl font-bold text-white">
                    {stats.pending}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-linear-to-br from-atlantis-500 to-atlantis-600 rounded-xl p-5 shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <CheckCircleIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white/90">
                    {t("lawyer.consultations.stats.accepted")}
                  </p>
                  <p className="text-2xl font-bold text-white">
                    {stats.accepted}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25 }}
              className="bg-linear-to-br from-tiber-500 to-tiber-700 rounded-xl p-5 shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <CalendarDaysIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white/90">
                    {t("lawyer.consultations.stats.completed")}
                  </p>
                  <p className="text-2xl font-bold text-white">
                    {stats.completed}
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-linear-to-br from-red-500 to-red-600 rounded-xl p-5 shadow-lg"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-white/20 rounded-xl">
                  <XCircleIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <p className="text-xs font-medium text-white/90">
                    {t("lawyer.consultations.stats.rejected")}
                  </p>
                  <p className="text-2xl font-bold text-white">
                    {stats.rejected}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Pending Alert */}
        {stats && stats.pending > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-dorado/10 border-2 border-dorado/30 rounded-xl p-4 mb-6 flex items-center gap-4"
          >
            <div className="p-2 bg-dorado/20 rounded-xl">
              <ExclamationTriangleIcon className="h-6 w-6 text-dorado" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-juridico">
                {t("lawyer.consultations.pendingAlert.title")}
              </p>
              <p className="text-sm text-tiber-700">
                {t("lawyer.consultations.pendingAlert.description")}
              </p>
            </div>
            <Button
              variant="ghost"
              onClick={() => setStatusFilter("pending")}
              className="text-dorado hover:text-juridico hover:bg-dorado/10 cursor-pointer"
            >
              {t("lawyer.consultations.viewPending")}
              <ArrowRightIcon className="h-4 w-4 ml-2" />
            </Button>
          </motion.div>
        )}

        {/* Filters Bar - responsive */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg border border-tiber-200 p-4 sm:p-5 mb-6"
        >
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
              <div className="sm:col-span-2 lg:col-span-1">
                <Input
                  type="text"
                  value={searchQuery}
                  leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t("lawyer.consultations.searchPlaceholder")}
                />
              </div>
              <Select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as FilterStatus)
                }
                options={[
                  { value: "all", label: t("common.allStatuses") },
                  { value: "pending", label: t("lawyer.consultations.status.pending") },
                  { value: "accepted", label: t("lawyer.consultations.status.accepted") },
                  { value: "completed", label: t("lawyer.consultations.status.completed") },
                  { value: "rejected", label: t("lawyer.consultations.status.rejected") },
                  { value: "cancelled", label: t("lawyer.consultations.status.cancelled") },
                ]}
              />
              <Select
                value={urgencyFilter}
                onChange={(e) =>
                  setUrgencyFilter(e.target.value as FilterUrgency)
                }
                options={[
                  { value: "", label: t("lawyer.consultations.filters.allUrgency") },
                  { value: "urgent", label: t("lawyer.consultations.urgency.urgent") },
                  { value: "high", label: t("lawyer.consultations.urgency.high") },
                  { value: "normal", label: t("lawyer.consultations.urgency.normal") },
                  { value: "low", label: t("lawyer.consultations.urgency.low") },
                ]}
              />
              <Select
                value={practiceAreaFilter}
                onChange={(e) => setPracticeAreaFilter(e.target.value)}
                options={[
                  { value: "", label: t("lawyer.consultations.filters.allAreas") },
                  ...practiceAreas.map((area) => ({
                    value: area.code,
                    label: area.name,
                  })),
                ]}
              />
            </div>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="shrink-0 p-3 bg-tiber-100 hover:bg-tiber-200 rounded-xl transition-colors duration-200 disabled:opacity-50 focus-visible:ring-2 focus-visible:ring-tiber-500 focus-visible:ring-offset-2"
              title={t("common.refresh")}
            >
              <ArrowPathIcon
                className={`h-5 w-5 text-tiber-700 ${refreshing ? "animate-spin" : ""}`}
              />
            </button>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          {consultations.length === 0 ? (
            <div className="bg-white rounded-xl shadow-lg border border-tiber-200 p-12 sm:p-16 text-center">
              <div className="max-w-md mx-auto">
                <div className="p-5 bg-tiber-100 rounded-2xl inline-flex mb-6">
                  <ChatBubbleLeftRightIcon className="h-16 w-16 text-tiber-600" />
                </div>
                <h3 className="text-xl font-semibold text-juridico mb-2">
                  {searchQuery ||
                  statusFilter !== "all" ||
                  urgencyFilter ||
                  practiceAreaFilter
                    ? t("lawyer.consultations.noResults")
                    : t("lawyer.consultations.empty.title")}
                </h3>
                <p className="text-tiber-700 mb-8 leading-relaxed">
                  {searchQuery ||
                  statusFilter !== "all" ||
                  urgencyFilter ||
                  practiceAreaFilter
                    ? t("lawyer.consultations.noResultsDescription")
                    : t("lawyer.consultations.empty.description")}
                </p>
                {(searchQuery ||
                  statusFilter !== "all" ||
                  urgencyFilter ||
                  practiceAreaFilter) && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSearchQuery("");
                      setStatusFilter("all");
                      setUrgencyFilter("");
                      setPracticeAreaFilter("");
                    }}
                  >
                    {t("common.clearFilters")}
                  </Button>
                )}
              </div>
            </div>
          ) : (
            <ConsultationsTable
              consultations={consultations}
              onView={handleViewConsultation}
              onAccept={handleAccept}
              onReject={handleReject}
              loading={loading && consultations.length > 0}
            />
          )}
        </motion.div>

        {/* Accepted Consultations - Convert to Case Section */}
        {consultations.filter((c) => c.status === "accepted").length > 0 &&
          statusFilter === "all" && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-8"
            >
              <h2 className="text-lg font-semibold text-juridico mb-4 flex items-center gap-2">
                <BriefcaseIcon className="h-5 w-5 text-tiber-600" />
                {t("lawyer.consultations.readyToConvert")}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {consultations
                  .filter((c) => c.status === "accepted")
                  .slice(0, 6)
                  .map((consultation) => (
                    <motion.div
                      key={consultation.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-white rounded-xl shadow-lg border border-tiber-200 p-5 hover:border-tiber-300 hover:shadow-xl transition-all duration-200"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="font-medium text-juridico">
                            {consultation.client_profiles?.profiles?.full_name}
                          </p>
                          <p className="text-sm text-titan capitalize">
                            {t(
                              `lawyer.practiceAreas.${consultation.practice_area}`,
                              consultation.practice_area,
                            )}
                          </p>
                        </div>
                        <span className="px-2.5 py-1 bg-atlantis-100 text-atlantis-800 text-xs font-medium rounded-full">
                          {t("lawyer.consultations.status.accepted")}
                        </span>
                      </div>
                      <p className="text-sm text-tiber-700 line-clamp-2 mb-4">
                        {consultation.description}
                      </p>
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full"
                        onClick={() => handleConvertToCase(consultation)}
                      >
                        <BriefcaseIcon className="h-4 w-4 mr-2" />
                        {t("lawyer.consultations.convertToCase")}
                      </Button>
                    </motion.div>
                  ))}
              </div>
            </motion.div>
          )}
      </div>

      {/* Modals */}
      <ConsultationDetailModal
        consultation={consultationViewModal}
        isOpen={!!consultationViewModal}
        onClose={() => setConsultationViewModal(null)}
        onConvertToCase={handleConvertToCase}
        showConvertButton={true}
      />
      <CreateCaseModal
        isOpen={isCreateCaseModalOpen}
        onClose={() => {
          setIsCreateCaseModalOpen(false);
          setSelectedConsultationDetails(null);
        }}
        onSuccess={handleCaseCreated}
        preselectedClientId={selectedConsultationDetails?.client_id}
      />
    </div>
  );
};
